import type { AdminLogDetails, AdminLogEntry } from "@/server/admin/logEntries";
import { resolveLogDetails } from "@/server/admin/logEntries";
import type { MemoryTelemetryEvent } from "@/server/admin/types";

export type LogDetailRow = {
  label: string;
  value: string;
};

const formatLatency = (latencyMs: number) => {
  if (latencyMs < 1000) return `${latencyMs}ms`;

  return `${(latencyMs / 1000).toFixed(1)}s`;
};

const kindLabel: Record<AdminLogDetails["kind"], string> = {
  context: "Context snapshot",
  tool_call: "Tool call",
  step: "Pipeline step",
  run_start: "Run started",
  stacked: "Stacked messages",
  run_complete: "Run completed",
  run_fail: "Run failed",
  memory: "Memory",
};

const memoryEventLabel: Record<MemoryTelemetryEvent, string> = {
  idle_flush: "Idle flush",
  idle_flush_failed: "Idle flush failed",
  sweep_started: "Sweep started",
  sweep_failed: "Sweep failed",
  prune_compact: "Prune compact",
  prune_compact_failed: "Prune compact failed",
  rolling_summary_ok: "Rolling summary updated",
  rolling_summary_failed: "Rolling summary failed",
};

const describeDetails = (details: AdminLogDetails): LogDetailRow[] => {
  switch (details.kind) {
    case "context": {
      const rows: LogDetailRow[] = [
        { label: "Type", value: kindLabel.context },
        { label: "Estimated tokens", value: `~${details.estimatedTokens.toLocaleString()}` },
        { label: "Messages", value: String(details.conversationHistoryCount) },
        {
          label: "Summary",
          value: details.rollingSummary
            ? `${details.rollingSummaryChars.toLocaleString()} chars`
            : "None",
        },
        { label: "Memories", value: String(details.turbopufferMemoryCount) },
        { label: "Event context", value: details.hasEventContext ? "Yes" : "No" },
      ];

      if (details.guestDetail) {
        rows.push({ label: "Guest", value: details.guestDetail });
      }

      return rows;
    }
    case "tool_call": {
      const rows: LogDetailRow[] = [
        { label: "Type", value: kindLabel.tool_call },
        { label: "Tool", value: details.name },
        { label: "Arguments", value: details.argsPreview || "(empty)" },
        { label: "Result", value: `${details.resultChars.toLocaleString()} chars` },
      ];

      if (details.resultPreview) {
        rows.push({ label: "Preview", value: details.resultPreview });
      }

      if (details.error) {
        rows.push({ label: "Error", value: details.error });
      }

      return rows;
    }
    case "step": {
      const rows: LogDetailRow[] = [
        { label: "Type", value: kindLabel.step },
        { label: "Step", value: details.step },
      ];

      if (details.detail) {
        rows.push({ label: "Detail", value: details.detail });
      }

      return rows;
    }
    case "run_start":
      return [
        { label: "Type", value: kindLabel.run_start },
        { label: "Inbound", value: details.inboundPreview },
      ];
    case "stacked":
      return [
        { label: "Type", value: kindLabel.stacked },
        { label: "Count", value: String(details.stackedCount) },
      ];
    case "run_complete":
      return [
        { label: "Type", value: kindLabel.run_complete },
        { label: "Latency", value: formatLatency(details.latencyMs) },
      ];
    case "run_fail":
      return [
        { label: "Type", value: details.status === "fallback" ? "Run fallback" : kindLabel.run_fail },
        { label: "Error", value: details.error },
      ];
    case "memory": {
      const rows: LogDetailRow[] = [
        { label: "Type", value: kindLabel.memory },
        { label: "Event", value: memoryEventLabel[details.event] },
      ];

      if (details.contactId) {
        rows.push({ label: "Contact", value: details.contactId });
      }

      if (details.eventId) {
        rows.push({ label: "Event ID", value: details.eventId });
      }

      if (details.messageCount !== undefined) {
        rows.push({ label: "Messages", value: String(details.messageCount) });
      }

      if (details.summaryChars !== undefined) {
        rows.push({ label: "Summary", value: `${details.summaryChars.toLocaleString()} chars` });
      }

      if (details.deletedMessageCount !== undefined) {
        rows.push({ label: "Deleted", value: String(details.deletedMessageCount) });
      }

      if (details.idleThresholdMs !== undefined) {
        rows.push({ label: "Idle threshold", value: `${details.idleThresholdMs.toLocaleString()}ms` });
      }

      if (details.sweepIntervalMs !== undefined) {
        rows.push({ label: "Sweep interval", value: `${details.sweepIntervalMs.toLocaleString()}ms` });
      }

      if (details.error) {
        rows.push({ label: "Error", value: details.error });
      }

      return rows;
    }
    default: {
      const _exhaustive: never = details;
      return [{ label: "Type", value: String(_exhaustive) }];
    }
  }
};

export const describeLogEntry = (entry: AdminLogEntry): LogDetailRow[] => {
  const details = resolveLogDetails(entry);

  if (details) {
    return describeDetails(details);
  }

  return [{ label: "Description", value: entry.message }];
};
